/**
 * ProtectedRoute
 * Shared route guard used in AppRoutes for auth + role authorization.
 *
 * Flow:
 * 1) while auth state is loading -> render LoadingState
 * 2) if no signed-in user -> redirect to login route with `from` return path
 * 3) if role is not allowed -> redirect to `forbiddenTo`
 * 4) otherwise -> render nested route (`Outlet`)
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import LoadingState from './common/LoadingState';

export default function ProtectedRoute({
  allowedRoles = [],
  redirectTo = '/admin-login',
  forbiddenTo = '/',
}) {
  const location = useLocation();
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <LoadingState label="Checking access..." />;
  }

  if (!user) {
    const returnPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={redirectTo} replace state={{ from: returnPath }} />;
  }

  const isRoleAllowed =
    allowedRoles.length === 0 || allowedRoles.includes(profile?.role);

  if (!isRoleAllowed) {
    return <Navigate to={forbiddenTo} replace />;
  }

  return <Outlet />;
}
